import { useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Spinner,
  Subtitle2,
  Textarea,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { ArrowLeft20Regular, Edit20Regular } from '@fluentui/react-icons'
import {
  useGateReview,
  useProduct,
  useProductHistory,
  useUpdateProduct,
} from '../hooks/useProducts'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { useToast } from '../components/ToastProvider'
import { apiErrorMessage } from '../lib/apiError'
import { gateStatusLabel } from '../lib/labels'
import { bayerColors } from '../theme'
import type { GateAction } from '../types/product'
import { LIFECYCLE_LABELS } from './ProductList'

const GATE_ACTION_LABEL: Record<GateAction, string> = {
  extend: 'Extend',
  pass: 'Pass',
  fail: 'Fail',
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString()
}

const useStyles = makeStyles({
  root: { maxWidth: '900px', margin: '0 auto' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' },
  meta: { color: tokens.colorNeutralForeground3, fontSize: '13px', marginTop: '4px' },
  badges: { display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap' },
  card: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '16px',
    marginTop: '16px',
  },
  sectionLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#616161',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '8px',
  },
  kv: { display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: '8px', columnGap: '12px', fontSize: '14px' },
  k: { color: tokens.colorNeutralForeground3 },
  teamRow: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '14px' },
  formField: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' },
  formLabel: { fontSize: '12px', fontWeight: 600, color: '#424242' },
  state: { display: 'flex', alignItems: 'center', gap: '8px', padding: '48px 16px', justifyContent: 'center' },
})

export function ProductDetail() {
  const styles = useStyles()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const effectiveUser = useEffectiveUser()
  const { showToast } = useToast()

  const isAdmin = effectiveUser?.role === 'admin' || effectiveUser?.role === 'super_admin'

  const { data: product, isLoading, error } = useProduct(id)
  const updateMutation = useUpdateProduct()
  const gateMutation = useGateReview()
  const { data: history } = useProductHistory(id)

  const [editOpen, setEditOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [gateAction, setGateAction] = useState<GateAction | null>(null)
  const [gateNote, setGateNote] = useState('')

  function submitGate() {
    if (!product || !gateAction) return
    gateMutation.mutate(
      { id: product.id, body: { action: gateAction, note: gateNote.trim() || undefined } },
      {
        onSuccess: () => {
          showToast({ title: `Gate ${GATE_ACTION_LABEL[gateAction].toLowerCase()}ed`, intent: 'success' })
          setGateAction(null)
          setGateNote('')
        },
        onError: (e) =>
          showToast({ title: 'Gate review failed', body: apiErrorMessage(e), intent: 'error' }),
      },
    )
  }

  function openEdit() {
    if (!product) return
    setName(product.name)
    setDescription(product.description ?? '')
    setEditOpen(true)
  }

  function submitEdit() {
    if (!product || !name.trim()) return
    updateMutation.mutate(
      // Send the trimmed description as-is (including '') so it can be cleared;
      // `|| undefined` would drop an emptied field and make clearing a no-op.
      { id: product.id, body: { name: name.trim(), description: description.trim() } },
      {
        onSuccess: () => {
          showToast({ title: 'Product updated', intent: 'success' })
          setEditOpen(false)
        },
        onError: (e) =>
          showToast({ title: 'Update failed', body: apiErrorMessage(e), intent: 'error' }),
      },
    )
  }

  function toggleArchive() {
    if (!product) return
    const archiving = !product.archived_at
    updateMutation.mutate(
      { id: product.id, body: { archived: archiving } },
      {
        onSuccess: () =>
          showToast({ title: archiving ? 'Product archived' : 'Product restored', intent: 'success' }),
        onError: (e) =>
          showToast({ title: 'Update failed', body: apiErrorMessage(e), intent: 'error' }),
      },
    )
  }

  if (isLoading) {
    return (
      <div className={styles.state} data-testid="product-detail-loading">
        <Spinner size="tiny" />
        <Body1>Loading…</Body1>
      </div>
    )
  }
  if (error || !product) {
    return (
      <div className={styles.state} role="alert" data-testid="product-detail-error">
        <Body1>Product not found.</Body1>
      </div>
    )
  }

  // Only show manage actions for products the caller can actually write: admin
  // anywhere, council within their own FA. Read access is broader (any council),
  // so gating the buttons avoids offering an action that would 403.
  const canManage =
    isAdmin ||
    (effectiveUser?.role === 'council' &&
      effectiveUser?.focus_area_code === product.focus_area_code)

  return (
    <section className={styles.root} aria-label="Product detail">
      <Button
        appearance="subtle"
        size="small"
        icon={<ArrowLeft20Regular />}
        onClick={() => navigate('/products')}
        data-testid="product-back"
      >
        Products
      </Button>

      <header className={styles.head} style={{ marginTop: '8px' }}>
        <div>
          <Title2 as="h1" style={{ color: '#242424' }}>
            {product.name}
          </Title2>
          <div className={styles.meta}>
            {product.public_id ?? `#${product.id}`} · {product.origin}
          </div>
          <div className={styles.badges}>
            <Badge appearance="tint" color="brand">
              {product.focus_area_code}
            </Badge>
            <Badge appearance="tint" data-testid="product-detail-stage">
              {LIFECYCLE_LABELS[product.lifecycle_stage]}
            </Badge>
            {product.archived_at && (
              <Badge appearance="tint" color="danger" data-testid="product-archived-flag">
                Archived
              </Badge>
            )}
          </div>
        </div>
        {canManage && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              appearance="secondary"
              icon={<Edit20Regular />}
              onClick={openEdit}
              data-testid="product-edit"
            >
              Edit
            </Button>
            <Button
              appearance="subtle"
              onClick={toggleArchive}
              disabled={updateMutation.isPending}
              data-testid="product-archive-toggle"
            >
              {product.archived_at ? 'Restore' : 'Archive'}
            </Button>
          </div>
        )}
      </header>

      <div className={styles.card}>
        <div className={styles.sectionLabel}>Overview</div>
        <Body1 style={{ whiteSpace: 'pre-wrap', marginBottom: '16px' }}>
          {product.description || <span style={{ color: '#9e9e9e' }}>No description.</span>}
        </Body1>
        <div className={styles.kv}>
          <div className={styles.k}>Lifecycle stage</div>
          <div>{LIFECYCLE_LABELS[product.lifecycle_stage]}</div>
          <div className={styles.k}>Gate status</div>
          <div>{product.gate_status ? gateStatusLabel(product.gate_status) : '— (gate review not started)'}</div>
          <div className={styles.k}>Source demand</div>
          <div>
            {product.source_demand_id ? (
              <RouterLink
                to={`/demand/${product.source_demand_id}`}
                data-testid="product-source-demand-link"
                style={{ color: bayerColors.navyMid }}
              >
                #{product.source_demand_id}
              </RouterLink>
            ) : (
              <span style={{ color: '#9e9e9e' }}>— (manual)</span>
            )}
          </div>
        </div>
      </div>

      <div className={styles.card} data-testid="product-gate-panel">
        <div className={styles.sectionLabel}>Gate review</div>
        {product.lifecycle_stage === 'poc' || product.lifecycle_stage === 'mvp' ? (
          <>
            <div className={styles.kv} style={{ marginBottom: '12px' }}>
              <div className={styles.k}>Status</div>
              <div>
                {product.gate_status ? (
                  <Badge
                    appearance="tint"
                    color={
                      product.gate_status === 'overdue'
                        ? 'danger'
                        : product.gate_status === 'due'
                          ? 'warning'
                          : 'success'
                    }
                    data-testid="gate-status-badge"
                  >
                    {gateStatusLabel(product.gate_status)}
                  </Badge>
                ) : (
                  '—'
                )}
              </div>
              <div className={styles.k}>Due</div>
              <div data-testid="gate-due">{fmtDate(product.gate_due_at)}</div>
              <div className={styles.k}>Extensions</div>
              <div>{product.gate_extension_count}</div>
            </div>
            {canManage && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  appearance="secondary"
                  onClick={() => setGateAction('extend')}
                  data-testid="gate-extend"
                >
                  Extend 90d
                </Button>
                <Button
                  appearance="primary"
                  onClick={() => setGateAction('pass')}
                  data-testid="gate-pass"
                  style={{ background: bayerColors.navyMid, borderColor: bayerColors.navyMid }}
                >
                  {product.lifecycle_stage === 'poc' ? 'Pass → MVP' : 'Pass → Portfolio'}
                </Button>
                <Button appearance="subtle" onClick={() => setGateAction('fail')} data-testid="gate-fail">
                  Fail → Sunset
                </Button>
              </div>
            )}
          </>
        ) : (
          <Caption1 style={{ color: '#9e9e9e' }}>
            {product.lifecycle_stage === 'portfolio'
              ? 'Graduated to Portfolio — no active gate.'
              : 'Retired (Sunset) — no active gate.'}
          </Caption1>
        )}
        {history && history.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div className={styles.k} style={{ marginBottom: '6px' }}>
              History
            </div>
            {history.map((h) => (
              <div key={h.id} className={styles.teamRow} data-testid={`gate-history-${h.id}`}>
                <Caption1>
                  <strong>{h.action ?? 'change'}</strong>
                  {h.from_stage ? ` · ${h.from_stage} → ${h.to_stage}` : ` · ${h.to_stage}`}
                  {h.note ? ` — ${h.note}` : ''}
                </Caption1>
                <Caption1 style={{ color: '#9e9e9e' }}>{fmtDate(h.changed_at)}</Caption1>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.card}>
        <div className={styles.sectionLabel}>Team ({product.team.length})</div>
        {product.team.length === 0 ? (
          <Caption1 style={{ color: '#9e9e9e' }}>
            No team yet.
          </Caption1>
        ) : (
          product.team.map((m) => (
            <div key={m.user_id} className={styles.teamRow} data-testid={`product-team-${m.user_id}`}>
              <Subtitle2 as="span" style={{ fontWeight: 500 }}>
                {m.name ?? `User #${m.user_id}`}
              </Subtitle2>
              <Caption1>{m.email}</Caption1>
            </div>
          ))
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={(_, d) => setEditOpen(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Edit product</DialogTitle>
            <DialogContent>
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor="product-edit-name">
                  Name
                </label>
                <Input
                  id="product-edit-name"
                  value={name}
                  onChange={(_, d) => setName(d.value)}
                  data-testid="product-edit-name"
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor="product-edit-desc">
                  Description
                </label>
                <Textarea
                  id="product-edit-desc"
                  value={description}
                  onChange={(_, d) => setDescription(d.value)}
                  rows={4}
                  data-testid="product-edit-desc"
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                disabled={!name.trim() || updateMutation.isPending}
                onClick={submitEdit}
                data-testid="product-edit-save"
              >
                {updateMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={gateAction !== null} onOpenChange={(_, d) => (!d.open ? setGateAction(null) : undefined)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{gateAction ? `${GATE_ACTION_LABEL[gateAction]} gate` : 'Gate review'}</DialogTitle>
            <DialogContent>
              <Caption1 style={{ display: 'block', marginBottom: '12px' }}>
                {gateAction === 'extend' && 'Push the 90-day gate deadline out by another 90 days.'}
                {gateAction === 'pass' &&
                  (product.lifecycle_stage === 'poc'
                    ? 'Advance this product from PoC to MVP (starts a fresh 90-day gate).'
                    : 'Graduate this product to Portfolio (gate closed).')}
                {gateAction === 'fail' && 'Retire this product (move to Sunset).'}
              </Caption1>
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor="gate-note">
                  Note (optional — recorded in the gate history)
                </label>
                <Textarea
                  id="gate-note"
                  value={gateNote}
                  onChange={(_, d) => setGateNote(d.value)}
                  rows={3}
                  data-testid="gate-note"
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setGateAction(null)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                disabled={gateMutation.isPending}
                onClick={submitGate}
                data-testid="gate-confirm"
              >
                {gateMutation.isPending ? 'Saving…' : 'Confirm'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </section>
  )
}

export default ProductDetail
